import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import fonts from '@helpers/fonts';
import { Device, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import styled from 'styled-components';
const Container = styled.button `
  --doorSize: 18px;

  display: inline-flex;
  align-items: center;
  border: var(--borderWidth, 0) solid var(--colorBorder, var(--colorBg));
  background: var(--colorBg);
  background-image: linear-gradient(to right, var(--colorBgActive), var(--colorBgActive));
  background-repeat: no-repeat;
  background-size: var(--bgSize);
  color: var(--colorFg);
  height: 55px;
  padding: 0px 32px;
  text-decoration: none;
  ${transition('background-size, border-color, opacity')};
  user-select: none;
  width: auto;

  &[data-fill='right'] {
    background-position: left center;
  }

  &[data-fill='left'] {
    background-position: right center;
  }

  &[data-size='small'] {
    --doorSize: 10px;

    height: 36px;
    padding: 0 14px;
  }

  span& {
    button:focus &,
    a:focus & {
      outline: auto;
      outline-offset: 3px;
    }

    @media (hover: hover) or (pointer: fine) {
      button:hover &,
      a:hover & {
        border-color: var(--colorBorderActive, var(--colorBgActive));
        color: var(--colorFgActive);
        background-size: var(--bgSizeActive);
      }
    }
  }

  &:not(span) {
    cursor: pointer;
    outline: none;

    &.focus-ring {
      outline: auto;
      outline-offset: 5px;
    }

    @media (hover: hover) or (pointer: fine) {
      &:hover {
        border-color: var(--colorBorderActive, var(--colorBgActive));
        color: var(--colorFgActive);
        background-size: var(--bgSizeActive);
      }
    }
  }

  &[data-type='default'] {
    --borderWidth: 2px;

    --bgSize: 100% 100%;
    --colorFg: ${brand.black};
    --colorBg: transparent;
    --colorBorder: ${brand.yellow};

    --bgSizeActive: 0% 100%;
    --colorFgActive: ${brand.black};
    --colorBgActive: ${brand.yellow};
    --colorBorderActive: ${brand.yellow};

    [data-theme='transparent'] &,
    [data-theme='dark'] & {
      --colorFgActive: ${brand.white};
    }
  }

  &[data-type='plain'] {

    --borderWidth: 2px;

    --bgSize: 100% 100%;
    --colorFg: ${brand.black};
    --colorBg: transparent;
    --colorBorder: ${brand.yellow};

    --bgSizeActive: 0% 100%;
    --colorFgActive: ${brand.black};
    --colorBgActive: ${brand.yellow};
    --colorBorderActive: ${brand.yellow};
  }

  &[data-type='default-green'] {
    --borderWidth: 2px;

    --bgSize: 100% 100%;
    --colorFg: ${brand.black};
    --colorBg: transparent;
    --colorBorder: ${brand.green};

    --bgSizeActive: 0% 100%;
    --colorFgActive: ${brand.black};
    --colorBgActive: ${brand.green};
    --colorBorderActive: ${brand.green};

    [data-theme='transparent'] &,
    [data-theme='dark'] & {
      --colorFgActive: ${brand.white};
    }
  }

  &[data-type='default-alt'] {
    --borderWidth: 2px;

    --bgSize: 100% 100%;
    --colorFg: ${brand.black};
    --colorBg: transparent;
    --colorBorder: ${brand.yellow};

    --bgSizeActive: 0% 100%;
    --colorFgActive: ${brand.yellow};
    --colorBgActive: ${brand.yellow};
    --colorBorderActive: ${brand.yellow};
  }

  &[data-type='outline'] {
    --borderWidth: 2px;

    --bgSize: 0% 100%;
    --colorFg: ${brand.yellow};
    --colorBg: transparent;
    --colorBorder: ${brand.yellow};

    --bgSizeActive: 100% 100%;
    --colorFgActive: ${brand.black};
    --colorBgActive: ${brand.yellow};
    --colorBorderActive: ${brand.yellow};
  }

  &[data-type='outline-alt'] {
    --borderWidth: 2px;

    --bgSize: 0% 100%;
    --colorFg: ${brand.black};
    --colorBg: ${brand.white};
    --colorBorder: ${brand.black};

    --bgSizeActive: 100% 100%;
    --colorFgActive: ${brand.white};
    --colorBgActive: ${brand.black};
    --colorBorderActive: ${brand.black};

    [data-theme='dark'] & {
      --colorBorder: ${brand.white};
      --colorBorderActive: ${brand.white};
    }
  }

  &[data-type='outline-white'] {
    --borderWidth: 2px;

    --bgSize: 0% 100%;
    --colorBg: transparent;
    --colorFg: ${brand.white};
    --colorBorder: ${brand.white};

    --bgSizeActive: 100% 100%;
    --colorFgActive: ${brand.black};
    --colorBgActive: ${brand.white};
    --colorBorderActive: ${brand.white};
  }

  &[data-align='center'] {
    span {
      margin: 0 auto;
    }
  }

  &[data-icon-only='true'] {
    height: 55px;
    justify-content: center;
    padding: 0;
    width: 55px;
  }

  /* Increase specificity to override :not(span) style */
  &:disabled:disabled:disabled {
    background-size: 100% 100%;
    opacity: 0.5;
    pointer-events: none;
  }

  @media ${until(Device.TabletLarge)} {
    height: 45px;
    padding: 0px 12px;

    &[data-icon-only='true'] {
      height: 45px;
      width: 45px;
    }
  }

  /** door opening animation on button hover */
  &[data-type='default'][data-fill='right'],
  &[data-type='default-alt'][data-fill='right'],
  &[data-type='default-green'][data-fill='right'] {
    /* hide border to replace with clip path */
    border: 0;
    position: relative;

    &::before,
    &::after {
      content: '';
      display: block;
      position: absolute;
    }

    &::before {
      /* create semi-complete border using clip path */
      clip-path: polygon(
        0% 0,
        0% 100%,
        101% 100%,
        101% calc(50% + (var(--doorSize, 0) * 0.5)),
        calc(100% - var(--borderWidth, 0)) calc(50% + (var(--doorSize, 0) * 0.5)),
        calc(100% - var(--borderWidth, 0)) calc(100% - var(--borderWidth, 0)),
        var(--borderWidth, 0) calc(100% - var(--borderWidth, 0)),
        var(--borderWidth, 0) var(--borderWidth),
        calc(100% - var(--borderWidth, 0)) var(--borderWidth, 0),
        calc(100% - var(--borderWidth, 0)) calc(50% - (var(--doorSize, 0) * 0.5)),
        100% calc(50% - (var(--doorSize, 0) * 0.5)),
        100% 0%
      );
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: var(--colorBorder);
    }

    /* cover gap in clip path to create solid border */
    &::after {
      ${transition('transform')};

      width: var(--borderWidth, 0);
      height: calc(var(--doorSize, 0) + 2px);
      transform-origin: 0 0;
      top: calc(50% - (var(--doorSize, 0) * 0.5) - 1px);
      right: 0;
      background-color: var(--colorBorder);
    }

    @media (hover: hover) or (pointer: fine) {
      &:hover::after {
        transform: rotate(40deg);
      }
    }
  }

  @media print {
    display: none !important;
  }
`;
const Text = styled.span `
  --fontSize: 22px;
  --lineHeight: 21px;

  ${transition('color')};

  color: currentColor;
  ${fonts.altGothicNo2.regular};
  font-size: var(--fontSize);
  line-height: var(--lineHeight);
  text-decoration: none;
  text-transform: uppercase;
  white-space: nowrap;



  [data-icon-only='true'] & {
    ${srOnly};
  }
`;
const IconWrapper = styled.div `
  --size: 14px;
  --spacing: 12px;

  display: block;
  color: currentColor;
  height: var(--size);
  margin: 0;
  padding: 0;
  ${transition('color')};
  width: calc(var(--size) + var(--spacing));

  [data-icon-position='left'] & {
    margin-right: auto;
    order: -1;
    padding-right: var(--spacing);
  }

  [data-icon-position='right'] & {
    margin-left: auto;
    order: 1;
    padding-left: var(--spacing);
  }

  [data-icon-only='true'] & {
    color: currentColor;
    height: var(--size);
    margin: 0;
    padding: 0;
    width: var(--size);
  }
`;
const ButtonStyles = {
    Container,
    IconWrapper,
    Text,
};
export default ButtonStyles;
